import { get } from "lodash";

export interface Threshold {
  minorLower?: number;
  majorLower?: number;
  minorUpper?: number;
  majorUpper?: number;
  durationLower?: number;
  durationUpper?: number;
}

export interface Thresh {
  [key: string]: Threshold;
}

export interface InfoCol {
  key: string;
  text: string;
  icon: string;
  suffix?: string;
  ifNull?: string;
}

export interface InfoRow {
  title: string;
  columns: InfoCol[];
}

export interface ImageElement {
  chairMount0?: string;
}

export interface InstructionsPage {
  title: string;
  body: string;
  images: string[];
}

export type EquipmentType =
  | "aerasone_compressor"
  | "aerasone_vacuum"
  | "compressor"
  | "vacuum"
  | "chair"
  | "handpiece"
  | "sterilizer";

export function EquipmentTypeName(eq: EquipmentType) {
  return eq === "aerasone_compressor"
    ? "Compressor (Aeras One)"
    : eq === "aerasone_vacuum"
      ? "Vacuum (Aeras One)"
      : eq === "chair"
        ? "Chair"
        : eq === "compressor"
          ? "Compressor"
          : eq === "handpiece"
            ? "Handpiece"
            : eq === "sterilizer"
              ? "Sterilizer"
              : eq === "vacuum"
                ? "Vacuum"
                : "";
}
// AER-1713: the Forest control board is shared by the 6400 and by the non-smart
// 5400 / Chair Replacement Kit units, so all three carry control-board firmware,
// control-board hardware and daughterboard firmware. The View / Edit Model
// screens and the manufacturing firmware-check screen previously hardcoded
// '6400', which hid those fields (and compared against the wrong data) for
// 5400 / CRK even though the values are stored on their model records.
export const BOARD_VERSION_MODEL_IDS = ["6400", "5400", "CRK"];

export const modelHasBoardVersions = (modelOrId?: string | Model): boolean => {
  const id = typeof modelOrId === "string" ? modelOrId : get(modelOrId, "id", "");
  return BOARD_VERSION_MODEL_IDS.includes(id as string);
};

// AER-1676 (D1): 5400 and CRK are non-smart Forest chairs. DentalEZ QAs them by
// attaching a reused Particle board only to verify factory-reset, print a test
// PDF, then the record is auto-deleted after ~24h. This shared helper mirrors the
// existing hardcoded '6400' convention. Accepts a model id or a Model object.
// AER-1796: lives here rather than in src/utils so the server's createModel can
// derive a non-smart chair's serial type codes without importing the UI utils.
export const NON_SMART_CHAIR_MODEL_IDS = ["5400", "CRK"];

export const isNonSmartChair = (modelOrId?: string | Model): boolean => {
  const id =
    typeof modelOrId === "string" ? modelOrId : (get(modelOrId, "id", "") as string);
  return NON_SMART_CHAIR_MODEL_IDS.includes(id);
};

export interface Model {
  id: string;
  [key: string]:
  | string
  | string[]
  | number
  | undefined
  | Thresh
  | InfoRow[]
  | ImageElement
  | boolean
  | InstructionsPage[];
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;

  control_board_fw?: string;
  control_board_hw?: string;
  description: string;
  family?: string;
  fw_version?: string;
  heads: number;
  // array of sensor IDs
  ignoreAlerts?: string[];
  image?: string;
  images?: ImageElement;
  infoRows?: InfoRow[];
  isActive?: number;
  manufacturer?: string;
  name: string;
  displayName: string; // "Model ID" to be displayed to the user - does not need to be unique
  modelNumber?: string;
  oldModel?: string;

  showMotor?: boolean;
  showOnManu?: boolean;
  // AER-1687: when false, the model is excluded from customer-facing dropdowns
  // (getActiveModels + reporting Model filter). Defaults to true when absent.
  showInDropdowns?: boolean;
  showTandem?: boolean;
  // AER-1795: serial-number type codes this model's units may carry (e.g. ['CR', 'CS']);
  // ['*'] means the model has no DentalEZ serial format. Defaults per type live in
  // src/_shared/lib/sn-type-codes.ts, and createModel fills them in when absent.
  snTypeCodes?: string[];
  thresholds?: Thresh;
  type: EquipmentType;

  instructions?: InstructionsPage[];
}

export class _Model {
  constructor(props?: Model) {
    for (let key in props) {
      this[key] = props[key];
    }
  }
  id = "";
  [key: string]:
  | string
  | string[]
  | number
  | undefined
  | Thresh
  | InfoRow[]
  | ImageElement
  | boolean
  | InstructionsPage[];
  createdAt = new Date().toISOString();
  createdBy = undefined;
  updatedAt = new Date().toISOString();
  updatedBy = undefined;

  control_board_fw?: string = undefined;
  control_board_hw?: string = undefined;
  description = "";
  family? = "";
  fw_version?: string = undefined;
  heads = 0;
  ignoreAlerts?: string[] = undefined;
  image? = "";
  images?: ImageElement = undefined;
  isActive = 0;
  manufacturer? = "";
  type: EquipmentType = "compressor";
  name = "";
  displayName = "";
  modelNumber = "";
  infoRows?: InfoRow[] = undefined;
  showMotor?: boolean = undefined;
  showOnManu?: boolean = undefined;
  showInDropdowns?: boolean = undefined;
  showTandem?: boolean = undefined;
  snTypeCodes?: string[] = undefined;
  thresholds?: Thresh = {};
  instructions?: InstructionsPage[] = undefined;
}

export const DefaultInfoRows = (etype: string) => {
  const options = {
    aerasone_compressor: [
      {
        "columns": [
          {
            "icon": "batteryHalf",
            "key": "BP",
            "show": "opts.sensorInfo.BP < 95 && opts.sensorInfo.PS == 0",
            "suffix": "%",
            "text": "Battery Capacity"
          },
          {
            "icon": "gaugeMed",
            "key": "TP",
            "suffix": " PSI",
            "text": "Tank Pressure"
          },
          {
            "icon": "bolt",
            "key": "CC",
            "suffix": " Amps",
            "text": "Current"
          }
        ],
        "title": "Performance"
      },
      {
        "columns": [
          {
            "key": "bars"
          },
          {
            "icon": "thermometer",
            "key": "AT",
            "suffix": " F",
            "text": "Ambient Temperature"
          },
          {
            "icon": "dropletPercent",
            "key": "AH",
            "suffix": "%",
            "text": "Ambient Humidity"
          }
        ],
        "title": "Environment"
      }
    ],
    aerasone_vacuum: [
      {
        "columns": [
          {
            "icon": "batteryHalf",
            "key": "BP",
            "show": "opts.sensorInfo.BP < 95 && opts.sensorInfo.PS == 0",
            "suffix": "%",
            "text": "Battery Capacity"
          },
          {
            "icon": "gaugeMed",
            "key": "IP",
            "suffix": " inHg",
            "text": "Vacuum Level"
          },
          {
            "icon": "bolt",
            "key": "CC",
            "suffix": " Amps",
            "text": "Current"
          }
        ],
        "title": "Performance"
      },
      {
        "columns": [
          {
            "key": "bars"
          },
          {
            "icon": "thermometer",
            "key": "AT",
            "suffix": " F",
            "text": "Ambient Temperature"
          },
          {
            "icon": "dropletPercent",
            "key": "AH",
            "suffix": "%",
            "text": "Ambient Humidity"
          }
        ],
        "title": "Environment"
      }
    ],
  };

  return get(options, etype);
};
